import RecordCreateUpdateMenu from "./ImportDataWidgetMenu/RecordCreateUpdateMenu";
import CustomImportMenu from "./ImportDataWidgetMenu/CustomImportMenu";
import Constants from "utils/Constants";
import { atom } from "jotai";
import FieldUtils from "utils/FieldUtils";

// Generates payload for Complex/Subitem Fields. Pass providedFields if it is a subitem.
export function getBaseFields(fieldType, providedFields) {
  const fields = providedFields || Constants.COMPLEX_FIELDS[fieldType];

  return fields.reduce((obj, f) => {
    if (
      FieldUtils.isComplex(f?.FieldType) ||
      FieldUtils.isPartiallyComplex(f?.FieldType)
    ) {
      obj[f?.APIName] = {
        Type: "Identifier",
        Fields: [{ Fields: getBaseFields(f?.FieldType) }],
      };
    } else {
      obj[f?.APIName] = { Identifier: "" };
    }

    return obj;
  }, {});
}

// The type of actions this widget can reference during header mapping.
const compatibleActions = ["record-create-update"];

// Jotai state for all children of ImportDataWidget.
export const mappedHeadersAtom = atom({});

/**
 * ImportDataWidget - For configuring what will happen when a file is imported
 * @param {string} moduleAPIName - The module API name you want to import records into.
 * @param {function} onChange - Function that accepts the updated data from this component.
 * @param {Array<object>} actions - All the actions in this workflow.
 * @param {object} existingData - The existing data to be loaded into this component.
 * @param {Array} externalHeaders - External headers to be loaded into this component.
 */
const ImportDataWidget = ({
  moduleAPIName = null,
  onChange,
  actions,
  existingData,
  externalHeaders,
  isSubmodule = false,
  isSubitem = false,
  fieldSchema,
  parentRecord,
}) => {
  const manageMapValue = (mapValue, finalValue) => {
    if (mapValue?.Identifier) {
      finalValue.Source = "File";
      finalValue.SourceKey = mapValue.Identifier;
    } else if (mapValue?.ActionID) {
      finalValue.Source = "Action";
      finalValue.ActionID = mapValue.ActionID;
    } else if (mapValue?.Value) {
      finalValue.Source = "Value";
      finalValue.Value = mapValue.Value;
    } else if (mapValue?.Dynamic) {
      finalValue.Source = "Dynamic";
      finalValue.Value = mapValue.Dynamic;
      finalValue.ParentSource = mapValue.ParentSource;
    } else {
      finalValue.Source = null;
    }
  };

  const manageItemValue = (item) => {
    if (item?.Source === "File") {
      return { Identifier: item.SourceKey };
    } else if (item?.Source === "Action") {
      return { ActionID: item.ActionID };
    } else if (item?.Source === "Value") {
      return { Value: item.Value };
    } else if (item?.Source === "Dynamic") {
      return { Dynamic: item.Dynamic };
    } else {
      return { Identifier: "" };
    }
  };

  const ActionsMenuSwitch = () => {
    if (existingData) {
      // From Workflows
      switch (existingData.Type) {
        case "record-create-update":
          return (
            <RecordCreateUpdateMenu
              moduleAPIName={moduleAPIName}
              onChange={onChange}
              actions={actions}
              existingData={existingData}
              externalHeaders={externalHeaders}
              mappedHeadersAtom={mappedHeadersAtom}
              compatibleActions={compatibleActions}
              manageMapValue={manageMapValue}
              manageItemValue={manageItemValue}
              usesActions={true}
            />
          );
        default:
          return (
            <RecordCreateUpdateMenu
              moduleAPIName={moduleAPIName}
              onChange={onChange}
              actions={actions}
              existingData={existingData}
              externalHeaders={externalHeaders}
              mappedHeadersAtom={mappedHeadersAtom}
              compatibleActions={compatibleActions}
              manageMapValue={manageMapValue}
              manageItemValue={manageItemValue}
            />
          );
      }
    } else {
      return (
        <CustomImportMenu
          moduleAPIName={moduleAPIName}
          onChange={onChange}
          actions={actions}
          existingData={existingData}
          externalHeaders={externalHeaders}
          mappedHeadersAtom={mappedHeadersAtom}
          compatibleActions={compatibleActions}
          parentRecord={parentRecord}
          manageMapValue={manageMapValue}
          manageItemValue={manageItemValue}
          isSubmodule={isSubmodule}
          isSubitem={isSubitem}
          fieldSchema={fieldSchema}
        />
      );
    }
  };

  return ActionsMenuSwitch();
};

export default ImportDataWidget;
